/**
 * TikTok's official embed in an iframe that only mounts once its box scrolls near the viewport (keeps long feeds
 * light). Remount it with a `key` to switch videos — one iframe at a time.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/components/ui/utils';

export interface LazyTikTokEmbedProps {
  src: string;
  title: string;
  /** Classes / style for the wrapper box (it reserves the space before the iframe loads). */
  className?: string;
  style?: CSSProperties;
  iframeClassName?: string;
  width?: number;
  height?: number;
  /** false = mount the iframe immediately (e.g. inside an open drawer). */
  lazy?: boolean;
  rootMargin?: string;
  placeholder?: ReactNode;
  /** iframe `allow` list — drop `autoplay` for previews that must never start on their own. */
  allow?: string;
}

export function LazyTikTokEmbed({
  src,
  title,
  className,
  style,
  iframeClassName = 'block border-0',
  width,
  height,
  lazy = true,
  rootMargin = '400px 0px',
  placeholder = 'Loading player…',
  allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen',
}: LazyTikTokEmbedProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(!lazy || typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [visible, rootMargin]);
  return (
    <div ref={ref} className={cn('overflow-hidden bg-black', className)} style={style}>
      {visible ? (
        <iframe
          src={src}
          title={title}
          width={width}
          height={height}
          allow={allow}
          allowFullScreen
          loading="lazy"
          className={iframeClassName}
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-[12px] text-[#98a2b3]">{placeholder}</div>
      )}
    </div>
  );
}
